import './DashboardPage.scss';
// hidden for demo — no-op filter bar, restore when wired:
// import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Page } from '@Components/Page';
import { PAGE_NAMES } from '@Components/Page/page-constants';
import { Spinner } from '@Components/Spinner/Spinner';
// import { FilterBar } from '@Components/FilterBar/FilterBar';
// import { FilterBarTokens } from '@Components/FilterBar/filterbar-types';
import { ChartId, ResolvedRoleLevels } from '@blisspointmedia/bpm-types/dist/dashboarding';
import { Role } from '@blisspointmedia/bpm-types/dist/Users';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import {
  useCanEditDashboard,
  useDashboard,
  useUpdateDashboard,
  UNSAVED_ID
} from './hooks/useDashboard';
import { useSetError } from '../../redux/modals';
import { DashboardBuilder } from './DashboardBuilder';
import { DashboardViewProvider } from './DashboardViewContext';
import { DashboardConfig } from './dashboard-types';
import { useRecordPermissions } from '../Charts/hooks/useRecordPermissions';
import { FetchError } from '../../utils/fetch-error';

interface DashboardViewProps {
  dashboardId: string;
}

// One dashboard: loads its layout, renders the editable grid, and persists changes. Mounted by
// DashboardPage when the route carries a dashboard id; the bare /dashboard route shows the list.
export const DashboardView = ({ dashboardId }: DashboardViewProps) => {
  const navigate = useNavigate();
  const { cid } = useCompanyInfo();
  const { data: config, isLoading, isError, error } = useDashboard(cid, dashboardId);
  const { mutate: updateDashboard, mutateAsync: updateDashboardAsync } = useUpdateDashboard(
    cid,
    dashboardId
  );
  const setError = useSetError();

  // Worked out ONCE here, where the config already lives, and threaded down as a prop beside the
  // existing `isEditMode`. Deliberately not on DashboardViewProvider: that context is documented as
  // transient, non-persisted view-override state, and a permission is not that. The rule itself
  // lives beside the query (see useCanEditDashboard), because the agent drawer asks it too.
  const canEditDashboard = useCanEditDashboard(config);
  // Separately, this user's own role number — for the empty-list message far below, not for a gate.
  const { userRole } = useRecordPermissions(config);
  // hidden for demo — no-op filter bar, restore when wired:
  // const [filterTokens, setFilterTokens] = useState<FilterBarTokens>({ advanced: [], basic: [] });

  // Every dashboard write lands here, view-mode removals included. Report a failure: a failed
  // removal leaves the card off screen with Save/Discard to retry or bring it back.
  const handlePersist = (next: DashboardConfig, callbacks?: { onError?: () => void }) => {
    // Say so rather than returning silently: a silent drop would leave a removed card off screen
    // with no explanation at all.
    if (!canEditDashboard) {
      setError({ message: "You don't have edit access to this dashboard." });
      callbacks?.onError?.();
      return;
    }
    // No `roles` — an ordinary save must leave the stored levels alone (see saveDashboardConfig).
    updateDashboard(
      { config: next },
      {
        onError: e => {
          setError({ message: `Couldn't save this dashboard: ${e.message}`, reportError: e });
          callbacks?.onError?.();
        }
      }
    );
  };

  // Change who may see and who may change this dashboard.
  //
  // ⚠️ SENDS THE SAVED CONFIG — `config` straight off the query — NEVER the builder's buffered
  // edits. The builder holds layout and rename changes locally until an explicit "Save changes", so
  // passing anything else here would commit a half-finished layout as a side effect of changing the
  // audience. This is the dashboard's version of the chart page's working-copy hazard.
  //
  // Rethrows after reporting, which is this handler's half of `PermissionDropdown`'s contract: the
  // dialog stays open on the user's selection and tracks nothing when the save did not land.
  const handlePermissionChange = async (roles: ResolvedRoleLevels) => {
    if (!config) return;
    try {
      await updateDashboardAsync({ config, roles });
    } catch (e) {
      setError({
        message: `Couldn't change who can see this dashboard: ${e.message}`,
        reportError: e
      });
      throw e;
    }
  };

  const handleBack = () => navigate(`/${cid}/dashboard`);

  if (isLoading) {
    return (
      <Page title={PAGE_NAMES.DASHBOARD} pageType={PAGE_NAMES.DASHBOARD}>
        <Spinner />
      </Page>
    );
  }

  if (isError || !config) {
    // 403 means the dashboard exists and this user is not cleared for it — most often met through a
    // pasted link. "Failed to load dashboard" would suggest something went wrong and that retrying
    // might help; neither is true.
    const isRestricted = error instanceof FetchError && error.status === 403;
    return (
      <Page title={PAGE_NAMES.DASHBOARD} pageType={PAGE_NAMES.DASHBOARD}>
        <div className='dashboard-error'>
          {isRestricted
            ? "You don't have access to this dashboard. Ask its owner for access."
            : 'Failed to load dashboard.'}
        </div>
      </Page>
    );
  }

  // The company has dashboards, but the filtered list handed this user none — so `fetchDashboard`
  // fell through to a synthetic blank "Overview". Left alone that reads as "nobody has built one"
  // and offers an editable blank, which is both wrong and confusing.
  //
  // This is the ONE place the feature breaks its own silence about what a filter hid. Everywhere
  // else a shorter list says nothing, because a count would tell an external user how much
  // internal work exists for their account and there is no action attached to the number. Here
  // there is no list at all, so silence would be a false statement instead of an omission.
  //
  // Role-based rather than count-based on purpose: asking the server "how many did you hide?" is
  // exactly the disclosure being avoided. Admins and internal staff see essentially everything, so
  // for them an empty list really does mean empty.
  if (config.id === UNSAVED_ID && config.cards.length === 0 && userRole > Role.INTERNAL) {
    return (
      <Page title={PAGE_NAMES.DASHBOARD} pageType={PAGE_NAMES.DASHBOARD}>
        <div className='dashboard-error'>
          No dashboards have been shared with you for this client yet.
        </div>
      </Page>
    );
  }

  // Every chart currently placed on this dashboard — passed to the provider so it can filter a
  // URL-seeded per-chart override down to charts that actually exist here (see the prop's own
  // comment in DashboardViewContext).
  const chartIds = config.cards
    .map(c => c.config?.chartId)
    .filter((id): id is ChartId => id !== undefined);

  return (
    <Page title={config.name} pageType={PAGE_NAMES.DASHBOARD}>
      <div className='dashboard-page'>
        {/* hidden for demo — no-op filter bar (empty options, no-op onFilter), restore when wired:
        <FilterBar
          options={[]}
          tokens={filterTokens}
          onChange={setFilterTokens}
          onFilter={() => {}} // TODO: wire filter predicate to card data queries
          useLocalStorage={false}
        /> */}
        {/* Keyed by dashboard: the provider seeds itself from the saved window at mount, and
            DashboardPage keeps this component mounted across id changes — so revisiting an
            already-cached dashboard (isLoading never flips, nothing unmounts) would otherwise leave
            every card on the PREVIOUS dashboard's window. The remount also clears the per-chart
            overrides, which have no business surviving a switch either. */}
        <DashboardViewProvider
          key={dashboardId}
          savedDateConfig={config.defaultDateConfig}
          chartIds={chartIds}
          savedDashboardFilters={config.defaultFilters}
        >
          <DashboardBuilder
            config={config}
            onPersist={handlePersist}
            onBack={handleBack}
            canEdit={canEditDashboard}
            onPermissionChange={handlePermissionChange}
          />
        </DashboardViewProvider>
      </div>
    </Page>
  );
};
